import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/api/client";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { canWrite } from "@/lib/roles";
import ResumeNotes from "@/components/board/ResumeNotes";
import ResumeKeywords from "@/components/board/ResumeKeywords";
import ResumeArchive from "@/components/board/ResumeArchive";
import { X, ChevronLeft, ChevronRight, Download, CheckCircle2, ArrowRightCircle, Loader2, FileText, Archive } from "lucide-react";

const IMG = [".png", ".jpg", ".jpeg", ".webp"];

async function downloadDoc(doc) {
  try {
    const res = await api.get(`/documents/${doc.id}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = doc.filename; a.click();
    URL.revokeObjectURL(url);
  } catch (e) { toast.error(formatApiError(e)); }
}

function DocPreview({ doc }) {
  const [state, setState] = useState({ loading: true });
  useEffect(() => {
    let url = null; let dead = false;
    setState({ loading: true });
    const isWord = doc.ext === ".docx" || doc.ext === ".doc";
    const canInline = doc.ext === ".pdf" || IMG.includes(doc.ext);
    const p = isWord
      ? api.get(`/documents/${doc.id}/text`).then(({ data }) => ({ text: data.text, approximate: data.approximate }))
      : canInline
      ? api.get(`/documents/${doc.id}/download`, { responseType: "blob" }).then((res) => {
          url = URL.createObjectURL(new Blob([res.data], { type: doc.ext === ".pdf" ? "application/pdf" : res.data.type }));
          return { url };
        })
      : Promise.resolve({ unsupported: true });
    p.then((s) => !dead && setState(s)).catch(async (e) => {
      let msg = formatApiError(e);
      try { if (e.response?.data instanceof Blob) msg = JSON.parse(await e.response.data.text()).detail || msg; } catch { /* keep default */ }
      if (!dead) setState({ error: msg });
    });
    return () => { dead = true; if (url) URL.revokeObjectURL(url); };
  }, [doc.id, doc.ext]);

  if (state.loading) return <div className="h-full flex items-center justify-center text-white/60"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (state.error) return <div data-testid="resume-viewer-error" className="h-full flex items-center justify-center text-sm text-white/70 px-6 text-center">{state.error}</div>;
  if (state.url && doc.ext === ".pdf") return <iframe data-testid="resume-viewer-pdf" title={doc.filename} src={state.url} className="w-full h-full bg-white rounded" />;
  if (state.url) return <div className="h-full overflow-auto flex items-start justify-center"><img data-testid="resume-viewer-image" src={state.url} alt={doc.filename} className="max-w-full rounded shadow" /></div>;
  if (state.text !== undefined) {
    return (
      <div className="h-full overflow-auto bg-white rounded p-6">
        {state.approximate && <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#7a5a1a] bg-[#FFF9C4] rounded px-2 py-1 mb-3 inline-block">Older Word format — text may be imperfect. Download for the original.</div>}
        <pre data-testid="resume-viewer-text" className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-[#15233B]">{state.text || "No readable text found in this file."}</pre>
      </div>
    );
  }
  return (
    <div data-testid="resume-viewer-unsupported" className="h-full flex flex-col items-center justify-center gap-3 text-white/70 text-sm">
      <FileText className="h-10 w-10" /> This file type can't be previewed. Download it to view.
    </div>
  );
}

function SideArrow({ dir, disabled, onClick, label }) {
  const Icon = dir === "prev" ? ChevronLeft : ChevronRight;
  return (
    <button data-testid={`resume-viewer-${dir}-candidate`} disabled={disabled} onClick={onClick} title={label} aria-label={label}
      className="shrink-0 h-10 w-10 sm:h-14 sm:w-14 rounded-full flex items-center justify-center bg-white/10 text-white border border-white/15 hover:bg-[#FFCB05] hover:text-[#15233B] hover:scale-105 active:scale-95 disabled:opacity-20 disabled:pointer-events-none transition-[background-color,color,transform] duration-150">
      <Icon className="h-6 w-6 sm:h-8 sm:w-8" />
    </button>
  );
}

export default function ResumeViewer({ card: startCard, docs: startDocs, archiveReasons = [], onClose, onMarked, onNoteSaved }) {
  const { user } = useAuth();
  const writable = canWrite(user);
  const [queue, setQueue] = useState([{ card: startCard, docs: startDocs }]);
  const [qi, setQi] = useState(0);
  const [i, setI] = useState(0);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState({ rating: null, body: "" });
  const [notes, setNotes] = useState([]);
  const [savingNote, setSavingNote] = useState(false);
  const touched = useRef({});
  const { card, docs } = queue[qi];
  const doc = docs[i] || docs[0];
  const hasPrev = qi > 0;
  const hasNext = qi < queue.length - 1;

  useEffect(() => {
    api.get(`/cards/${startCard.id}/resume-queue`).then(({ data }) => {
      const items = data.items || [];
      const idx = items.findIndex((x) => x.card.id === startCard.id);
      if (idx >= 0) { setQueue(items); setQi(idx); }
    }).catch(() => {});
  }, [startCard.id]);

  useEffect(() => {
    api.get(`/cards/${card.id}/notes`)
      .then(({ data }) => setNotes((data || []).filter((n) => n.kind === "resume_review").sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""))))
      .catch(() => setNotes([]));
  }, [card.id]);

  const updateCard = (c) => {
    setQueue((q) => q.map((x) => (x.card.id === c.id ? { ...x, card: c } : x)));
    if (c.id === startCard.id && c.status === "active") onMarked(c); else touched.current[c.id] = c;
  };

  const saveNote = async () => {
    if (!draft.rating && !draft.body.trim()) return null;
    setSavingNote(true);
    try {
      const { data } = await api.post(`/cards/${card.id}/notes`, { body: draft.body.trim(), kind: "resume_review", rating: draft.rating });
      setNotes((x) => [data.note, ...x]);
      setDraft({ rating: null, body: "" });
      if (card.id === startCard.id) onNoteSaved?.(data.note);
      return data.card;
    } finally { setSavingNote(false); }
  };
  const onSaveNote = () => saveNote().then((c) => { if (c) { toast.success("Note saved"); updateCard(c); } }).catch((e) => toast.error(formatApiError(e)));

  const close = (latest) => {
    const last = latest || queue[qi].card;
    onClose({ touched: Object.values(touched.current).filter((c) => c.id !== last.id), last });
  };

  const go = async (n) => {
    if (n < 0 || n >= queue.length || busy) return;
    try {
      const c = await saveNote();
      if (c) { toast.success("Note saved"); updateCard(c); }
    } catch (e) { toast.error(formatApiError(e)); return; }
    setQi(n); setI(0); setDraft({ rating: null, body: "" });
  };

  const moveOn = (data) => {
    if (hasNext) { setQi(qi + 1); setI(0); setDraft({ rating: null, body: "" }); } else close(data);
  };

  const goRef = useRef(go);
  goRef.current = go;
  const closeRef = useRef(close);
  closeRef.current = close;
  const qiRef = useRef(qi);
  qiRef.current = qi;
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") { e.stopPropagation(); closeRef.current(); return; }
      if (e.target.closest?.("input, textarea, select, [contenteditable='true']")) return;
      if (e.key === "ArrowRight") { e.preventDefault(); goRef.current(qiRef.current + 1); }
      if (e.key === "ArrowLeft") { e.preventDefault(); goRef.current(qiRef.current - 1); }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, []);

  const taskIdx = card.tasks_state.findIndex((t) => /resume review/i.test(t.title));
  const reviewed = taskIdx >= 0 && card.tasks_state[taskIdx].done;
  const atCapture = card.current_stage === 101 && card.status === "active";

  const mark = async (advance) => {
    setBusy(true);
    try {
      await saveNote();
      let { data } = reviewed ? { data: card } : await api.patch(`/cards/${card.id}/tasks/${taskIdx}`, { done: true });
      if (advance) data = (await api.post(`/cards/${card.id}/advance`)).data;
      toast.success(advance ? `${card.full_name} reviewed — moved to Screen` : "Resume marked reviewed");
      updateCard(data);
      if (advance) moveOn(data);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const archive = async (reason, note) => {
    setBusy(true);
    try {
      const body = [draft.body.trim(), note].filter(Boolean).join("\n") || `Archived — ${reason}`;
      const { data: n } = await api.post(`/cards/${card.id}/notes`, { body, kind: "resume_review", rating: "not_fit" });
      setNotes((x) => [n.note, ...x]);
      setDraft({ rating: null, body: "" });
      if (card.id === startCard.id) onNoteSaved?.(n.note);
      const { data } = await api.post(`/cards/${card.id}/archive`, { reason, note });
      toast.success(`${card.full_name} archived — ${reason}`);
      updateCard(data);
      moveOn(data);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const nextName = hasNext ? queue[qi + 1].card.full_name : "";
  const prevName = hasPrev ? queue[qi - 1].card.full_name : "";

  return (
    <div className="fixed inset-0 z-[60] bg-[#0B1424]/90 backdrop-blur-sm flex flex-col sm:flex-row" data-testid="resume-viewer">
      <div className="flex-1 flex flex-col p-3 sm:p-6 min-w-0 min-h-0">
        <div className="flex items-center gap-3 text-white mb-3">
          <FileText className="h-4 w-4 text-[#FFCB05] shrink-0" />
          <div className="text-sm font-bold truncate" data-testid="resume-viewer-filename">{doc.filename}</div>
          {docs.length > 1 && (
            <div className="flex items-center gap-1 ml-2 shrink-0">
              <button data-testid="resume-viewer-prev" disabled={i === 0} onClick={() => setI(i - 1)} title="Previous file" className="p-1 rounded hover:bg-white/10 disabled:opacity-30"><ChevronLeft className="h-4 w-4" /></button>
              <span className="text-xs text-white/60" data-testid="resume-viewer-count">File {i + 1} of {docs.length}</span>
              <button data-testid="resume-viewer-next" disabled={i === docs.length - 1} onClick={() => setI(i + 1)} title="Next file" className="p-1 rounded hover:bg-white/10 disabled:opacity-30"><ChevronRight className="h-4 w-4" /></button>
            </div>
          )}
          {queue.length > 1 && (
            <span data-testid="resume-viewer-candidate-count" className="ml-auto shrink-0 text-[11px] font-bold uppercase tracking-[0.15em] text-[#15233B] bg-[#FFCB05] rounded-full px-2.5 py-1">
              Candidate {qi + 1} of {queue.length}
            </span>
          )}
          <button data-testid="resume-viewer-close" onClick={() => close()} className={`${queue.length > 1 ? "" : "ml-auto"} p-1.5 rounded hover:bg-white/10`}><X className="h-5 w-5" /></button>
        </div>
        <div className="flex-1 min-h-0 flex items-center gap-2 sm:gap-4">
          {queue.length > 1 && <SideArrow dir="prev" disabled={!hasPrev || busy} onClick={() => go(qi - 1)} label={hasPrev ? `Previous candidate: ${prevName} (←)` : "First candidate"} />}
          <div key={`${card.id}-${doc.id}`} className="flex-1 h-full min-w-0 animate-in fade-in duration-200"><DocPreview doc={doc} /></div>
          {queue.length > 1 && <SideArrow dir="next" disabled={!hasNext || busy} onClick={() => go(qi + 1)} label={hasNext ? `Next candidate: ${nextName} (→)` : "Last candidate"} />}
        </div>
        {queue.length > 1 && <div className="hidden sm:block text-center text-[11px] text-white/45 mt-2">Use ← / → keys to move between candidates in this stage</div>}
      </div>
      <aside className="w-full sm:w-80 shrink-0 bg-white flex flex-col max-h-[50vh] sm:max-h-none overflow-y-auto">
        <div className="p-5 border-b border-[#15233B]/10">
          <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#1E73C9]">Resume review</div>
          <div className="text-lg font-extrabold text-[#15233B] leading-tight mt-1" data-testid="resume-viewer-candidate-name">{card.full_name}</div>
          <div className="text-xs text-[#15233B]/55 mt-0.5">{card.av_id} · {card.position || "Candidate"}</div>
        </div>
        <div className="p-5 space-y-3 sm:flex-1">
          <div className="text-xs text-[#15233B]/60">Uploaded {doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString() : "—"}{doc.uploaded_by ? ` by ${doc.uploaded_by}` : ""}</div>
          <button data-testid="resume-viewer-download" onClick={() => downloadDoc(doc)} className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-md border border-[#15233B]/15 text-sm font-bold text-[#15233B] hover:bg-[#15233B]/5">
            <Download className="h-4 w-4" /> Download
          </button>
          {card.status === "archived" && (
            <div data-testid="resume-viewer-archived" className="flex items-center gap-1.5 text-sm font-bold text-[#b91c1c] bg-[#b91c1c]/5 border border-[#b91c1c]/20 rounded-md px-3 py-2">
              <Archive className="h-4 w-4 shrink-0" /> Archived{card.archived_reason ? ` · ${card.archived_reason}` : ""}
            </div>
          )}
          {reviewed && (
            <div data-testid="resume-viewer-reviewed" className="flex items-center gap-1.5 text-sm font-bold text-[#166534] bg-[#166534]/5 border border-[#166534]/20 rounded-md px-3 py-2">
              <CheckCircle2 className="h-4 w-4" /> Resume reviewed
            </div>
          )}
          <div className="border-t border-[#15233B]/10 pt-3"><ResumeKeywords key={`${card.id}-${doc.id}`} card={card} doc={doc} writable={writable} onCard={updateCard} /></div>
          <div className="border-t border-[#15233B]/10 pt-3">
            <ResumeNotes draft={draft} setDraft={setDraft} notes={notes} saving={savingNote} onSave={onSaveNote} writable={writable} />
          </div>
        </div>
        {atCapture && taskIdx >= 0 && writable && (
          <div className="p-5 border-t border-[#15233B]/10 space-y-2">
            {!reviewed && (
              <button data-testid="resume-mark-reviewed" disabled={busy} onClick={() => mark(false)}
                className="w-full flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-md bg-[#15233B] text-white text-sm font-bold hover:bg-[#1E73C9] disabled:opacity-60 transition-colors">
                <CheckCircle2 className="h-4 w-4" /> Mark reviewed
              </button>
            )}
            <button data-testid="resume-mark-reviewed-advance" disabled={busy} onClick={() => mark(true)}
              className="w-full flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-md bg-[#FFCB05] text-[#15233B] text-sm font-extrabold hover:bg-[#FBC02D] disabled:opacity-60 transition-colors">
              <ArrowRightCircle className="h-4 w-4" /> {reviewed ? "Move to Screen" : "Mark reviewed & move to Screen"}
            </button>
            {hasNext && <div className="text-[11px] text-center text-[#15233B]/50" data-testid="resume-next-hint">Then opens {nextName}</div>}
          </div>
        )}
        {card.status === "active" && writable && archiveReasons.length > 0 && (
          <div className={`px-5 pb-5 ${atCapture && taskIdx >= 0 ? "" : "pt-5 border-t border-[#15233B]/10"}`}>
            <ResumeArchive key={card.id} reasons={archiveReasons} busy={busy} hasNext={hasNext} onConfirm={archive} />
          </div>
        )}
      </aside>
    </div>
  );
}
